import { Box, Divider, Typography } from '@mui/material';
import { ColumnBox, RowBox } from '../../styles/commonStyles';
import { styled, SxProps } from '@mui/material/styles';
import { ReactNode } from 'react';
import { ThemeTypography } from '../common/ThemeTypography';
import { theme } from '../../configs/theme';

/**
 * Props for the CardLayout component.
 */
type CardProps = {
  /**
   * Optional index number to display on the card.
   */
  index?: number;

  /**
   * Title text for the card.
   */
  title?: string;

  /**
   * Optional description text for the card.
   */
  description?: string;

  /**
   * Optional side component, such as an icon or button, to display on the right side of the card.
   */
  sideComponent?: ReactNode;

  /**
   * The content to display in the main body of the card.
   */
  children: ReactNode;
  sx?: SxProps;
};

/**
 * A card layout component that displays a title, optional description, and a content section.
 * It also supports an optional index and side component for additional functionality.
 *
 * @param {CardProps} props - The props for the CardLayout component.
 * @returns {ReactElement} The rendered CardLayout component.
 *
 * @example
 * <CardLayout
 *    index={1}
 *    title="Card Title"
 *    description="This is a description."
 *    sideComponent={<Button>Click Me</Button>}
 * >
 *    <ThemeTypography>Content goes here</ThemeTypography>
 * </CardLayout>
 */
export const CardLayout = ({
  index,
  title,
  description,
  children,
  sideComponent,
  sx,
}: CardProps) => {
  return (
    <CardContainer sx={sx}>
      {/* Title Section */}
      <RowBox sx={{ width: '100%', justifyContent: 'space-between' }}>
        <RowBox sx={{ padding: '15px', alignItems: 'center', gap: '0.8rem' }}>
          {/* Optional index display */}
          {index && (
            <IndexContainer>
              <ThemeTypography type="body2">{index}</ThemeTypography>
            </IndexContainer>
          )}

          {/* Title and description */}
          <ColumnBox>
            <ThemeTypography type="body2">{title}</ThemeTypography>
            <ThemeTypography type="italic">{description}</ThemeTypography>
          </ColumnBox>
        </RowBox>
        {/* Optional side component */}
        <RowBox sx={{ alignItems: 'center', paddingRight: '10px' }}>{sideComponent}</RowBox>
      </RowBox>
      <Divider />
      {/* Content Section */}
      <ColumnBox sx={{ padding: '15px' }} display={'flex'} flexDirection={'column'} gap={'8px'}>
        {children}
      </ColumnBox>
    </CardContainer>
  );
};

/**
 * Styled container for the index circle.
 */
const IndexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  background: theme.palette.customColors.softerGreen,
  padding: '10px',
  width: '30px',
  height: '30px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
}));

/**
 * Styled container for the entire card layout.
 */
const CardContainer = styled(Box)(() => ({
  background: theme.palette.customColors.cultured,
  borderRadius: '10px',
}));
